import React, { useState, useEffect, useMemo, useRef } from "react";
import { useDentalRole } from "../../hooks/useDentalRole";
import TreatmentTemplates from "./sections/TreatmentTemplates";
import { useSearchParams, useLocation } from "react-router-dom";
import Sidebar from "../../components/Sidebar";
import { useSidebar } from "../../contexts/SidebarContext";
import { SettingsSidebar } from "./SettingsSidebar";
import { SettingsHeader } from "./SettingsHeader";
import { searchSettings } from "./searchIndex";
import { ComingSoon } from "./sections/ComingSoon";
import { ProfileAccount } from "./sections/ProfileAccount";
import { FinanceBilling } from "./sections/FinanceBilling";
import { SystemPreferences } from "./sections/SystemPreferences";
import { AccessPersonalizations } from "./sections/AccessPersonalizations";
import { ClinicSettings } from "./sections/ClinicSettings";
import { ComplianceVerification } from "./sections/ComplianceVerification";
import { SmartAutomation } from "./sections/SmartAutomation";
import { DataImport } from "./sections/DataImport";
import { DataPrivacy } from "./sections/DataPrivacy";
import { SupportTickets } from "./sections/SupportTickets";
import ManageDevices from "./sections/ManageDevices";
import { SettingsAPI } from "../../services/settings.service";
import { useRole } from "../../hooks/useRole";
import { SettingsAccessProvider } from "./SettingsAccessContext";
import { useSupportUnread } from "../../hooks/useSupportUnread";

// ── Section registry ──────────────────────────────────────────────────────────
// Swap ComingSoon out for the real component as each section is built.

const SECTION_LABELS: Record<string, string> = {
  profile:      "Profile & Account",
  devices:      "Trusted Devices",
  clinic:       "Clinic Settings",
  access:       "Access & Personalizations",
  finance:      "Finance & Billing",
  privacy:      "Data & Privacy",
  import:       "Data Import",
  compliance:   "Compliance & Verification",
  system:       "System & Preferences",
  automation:   "Smart Automation",
  support:      "Support",
  templates:    "Treatment Templates",
};

// ─────────────────────────────────────────────────────────────────────────────

// Legacy role→sections gate. Only consulted when the backend did not return
// permission_codes (see useRole's permissionsKnown) — otherwise a section is
// visible exactly when the role holds its settings_<id>.view or .edit grant,
// which is what makes ticking a Settings box in the Role Catalog actually
// reveal the page.
export const SECTIONS_BY_ROLE: Record<string, Set<string>> = {
  Doctor:       new Set(["profile", "devices", "support"]),
  Receptionist: new Set(["profile", "devices", "support"]),
};

/** Section ids this user can open, in NAV order. */
function visibleSectionsFor(
  pageAccess: (section: string) => "none" | "view" | "edit",
  permissionsKnown: boolean,
  role: string,
): Set<string> | null {
  if (!permissionsKnown) return SECTIONS_BY_ROLE[role] ?? null; // null = show all
  return new Set(
    Object.keys(SECTION_LABELS).filter(id => pageAccess(id) !== "none"),
  );
}

const SettingsPage: React.FC = () => {
  const { isExpanded } = useSidebar();
  const { role, pageAccess, permissionsKnown } = useRole();
  const { summary: supportSummary } = useSupportUnread("clinic");
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Deep-link entry point: /settings?section=<id>&card=<cardId> or
  // navigate("/settings", { state: { section } }). Falls back to profile for
  // an unknown/absent section. Role gating still applies via safeSection below.
  const requestedSection =
    searchParams.get("section") ||
    (location.state as { section?: string } | null)?.section ||
    "profile";
  const [activeSection, setActiveSection] = useState(
    SECTION_LABELS[requestedSection] ? requestedSection : "profile"
  );
  const [searchQuery,   setSearchQuery]   = useState("");
  const [userName,      setUserName]      = useState<string | undefined>();
  const [avatarUrl,     setAvatarUrl]     = useState<string | undefined>();
  const [isSectionsOpen, setIsSectionsOpen] = useState(false);

  useEffect(() => {
    const state = location.state as { section?: string } | null;
    if (state?.section) setActiveSection(state.section);
  }, [location.state]);

  useEffect(() => {
    SettingsAPI.getProfile()
      .then(p =>{
        setUserName(`${p.first_name} ${p.last_name}`.trim())
        setAvatarUrl(p.photo_url ?? undefined);
      })
      .catch(() => { /* non-critical — avatar/name just stays empty */ });
  }, []);

  // If the current active section isn't granted, fall back to profile — which
  // every authenticated user can always open. Covers deep links
  // (?section=finance) as well as a permission being revoked mid-session.
  // Treatment Templates is self-scoped like Profile (a doctor's own saved
  // plans), so it carries no settings_<id> grant; it is shown to dentists only.
  const { isDentist } = useDentalRole();
  const allowedSet = useMemo(() => {
    const base = visibleSectionsFor(pageAccess, permissionsKnown, role);
    const set = new Set(base ?? Object.keys(SECTION_LABELS));
    if (isDentist) set.add("templates"); else set.delete("templates");
    return set;
  }, [pageAccess, permissionsKnown, role, isDentist]);
  const safeSection = allowedSet && !allowedSet.has(activeSection) ? "profile" : activeSection;

  const label = SECTION_LABELS[safeSection] ?? safeSection;
  // Drives EditableCardFooter for every card in the open section.
  const canEditSection = pageAccess(safeSection) === "edit";

  // ── Search → highlight nav + scroll to cards ────────────────────────────────
  const result = useMemo(() => searchSettings(searchQuery, safeSection), [searchQuery, safeSection]);

  // A card to scroll to once a section finishes rendering (used when the search
  // jumps to a different section than the one currently open).
  const pendingScroll = useRef<string | null>(null);

  const scrollToCard = (cardId: string) => {
    // rAF so the target exists after a section switch / re-render.
    requestAnimationFrame(() => {
      const el = document.getElementById(cardId);
      if (!el) return;
      el.scrollIntoView({ behavior: "smooth", block: "start" });
      // Brief highlight flash so the user sees what matched.
      el.classList.add("ring-2", "ring-blue-400");
      window.setTimeout(() => el.classList.remove("ring-2", "ring-blue-400"), 1600);
    });
  };

  // Deep-link: if the URL carried ?card=<id>, scroll to it once on mount
  // (the initial section is already set from the URL above).
  useEffect(() => {
    const card = searchParams.get("card");
    if (card) scrollToCard(card);
  // Run once on mount — deep-link params are read at entry only.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // After a programmatic section switch, scroll to the card that triggered it.
  useEffect(() => {
    if (pendingScroll.current) {
      scrollToCard(pendingScroll.current);
      pendingScroll.current = null;
    }
  }, [activeSection]);

  // Live: as the user types, scroll to the first matching card in the open
  // section (no section jump — keeps typing smooth).
  useEffect(() => {
    if (!searchQuery.trim()) return;
    const cards = result.cardsBySection[safeSection];
    if (cards?.length) scrollToCard(cards[0]);
  // result captures searchQuery + safeSection; no additional deps needed.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [result]);

  // Selecting a section while searching scrolls to its first matching card.
  const handleSelectSection = (id: string) => {
    const cards = result.cardsBySection[id];
    if (cards?.length) pendingScroll.current = cards[0];
    setActiveSection(id);
    setIsSectionsOpen(false);
  };

  // Enter in the search box → jump to the best match across all sections.
  const goToBestMatch = () => {
    const best = result.best;
    if (!best) return;
    if (best.section !== activeSection) {
      pendingScroll.current = best.cardId;
      setActiveSection(best.section);
    } else {
      scrollToCard(best.cardId);
    }
  };

  return (
    <div className="flex h-screen bg-gray-50 overflow-hidden">
      {/* App-level navigation */}
      <Sidebar />

      {/* Settings content area — starts at 0 because <Sidebar/> is fixed-positioned.
          The spacer below reserves exactly the collapsed app-sidebar width (72px) so
          the SettingsSidebar is never hidden underneath the app sidebar. */}
      <div className="flex-1 flex overflow-hidden pt-14 lg:pt-0">

        {/* Spacer tracks the live app sidebar width so settings never slides under it */}
        <div
          className="flex-shrink-0 hidden lg:block transition-all duration-300"
          style={{ width: isExpanded ? 240 : 72 }}
        />

        {/* Settings left nav — always-visible sidebar on lg:+, off-canvas drawer on mobile */}
        <SettingsSidebar
          active={safeSection}
          onSelect={handleSelectSection}
          search={searchQuery}
          highlighted={result.sections}
          allowedSections={allowedSet}
          supportUnread={supportSummary.total}
          isMobileOpen={isSectionsOpen}
          onMobileClose={() => setIsSectionsOpen(false)}
        />

        {/* Main panel */}
        <div className="flex-1 flex flex-col overflow-hidden">
          {/* Mobile-only section switcher — opens the SettingsSidebar drawer.
              Styled as a dropdown/select control (label + trailing chevron)
              rather than a hamburger icon, since a second hamburger-style
              button right under the app sidebar's own toggle read as a
              confusing duplicate of it. */}
          <button
            onClick={() => setIsSectionsOpen(true)}
            className="lg:hidden flex items-center justify-between gap-2 px-4 min-h-[44px] border-b border-gray-100 bg-white text-left flex-shrink-0"
          >
            <span className="flex items-center gap-2 min-w-0">
              <span className="text-[10px] font-semibold text-gray-400 uppercase tracking-wide flex-shrink-0">Section</span>
              <span className="text-sm font-medium text-gray-700 truncate">{label}</span>
            </span>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-gray-400 flex-shrink-0">
              <polyline points="6 9 12 15 18 9"/>
            </svg>
          </button>

          <SettingsHeader
            activeSection={safeSection}
            searchQuery={searchQuery}
            onSearch={setSearchQuery}
            onSubmitSearch={goToBestMatch}
            onProfile={() => setActiveSection("profile")}
            userName={userName}
            avatarUrl={avatarUrl}
          />

          <div className="flex-1 overflow-y-auto p-4 sm:p-6">
            <SettingsAccessProvider canEdit={canEditSection}>
              {!canEditSection && safeSection !== "profile" && safeSection !== "devices" && safeSection !== "templates" && (
                <div className="mb-4 flex items-center gap-2 rounded-lg border border-amber-200 bg-amber-50 px-3.5 py-2.5">
                  <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor"
                    strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"
                    className="text-amber-500 flex-shrink-0">
                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z"/><circle cx="12" cy="12" r="3"/>
                  </svg>
                  <p className="text-xs text-amber-800">
                    View-only access — ask an admin for edit rights on {label}.
                  </p>
                </div>
              )}
              {safeSection === "profile"  ? (
                 <ProfileAccount
                   onProfileChange={p => {
                     setUserName(`${p.first_name} ${p.last_name}`.trim());
                     setAvatarUrl(p.photo_url ?? undefined);
                   }}
                 />
               ) :
               safeSection === "devices"      ? <ManageDevices /> :
               safeSection === "finance"      ? <FinanceBilling /> :
               safeSection === "system"       ? <SystemPreferences userRole={role} /> :
               safeSection === "access"       ? <AccessPersonalizations /> :
               safeSection === "clinic"       ? <ClinicSettings /> :
               safeSection === "compliance"   ? <ComplianceVerification /> :
               safeSection === "automation"   ? <SmartAutomation /> :
               safeSection === "privacy"      ? <DataPrivacy /> :
               safeSection === "import"       ? <DataImport /> :
               safeSection === "support"      ? <SupportTickets /> :
               safeSection === "templates"    ? <TreatmentTemplates /> :
               <ComingSoon section={label} />
              }
            </SettingsAccessProvider>
          </div>
        </div>

      </div>
    </div>
  );
};

export default SettingsPage;
